<template>
  <div
    class="input-array-of-object"
    v-if="initialized"
    ref="arrayOfObjectWrapper"
  >
    <div
      v-for="(row, rowIndex) in v || []"
      :key="row.tempKey"
      class="card mb-2 mt-1 input-array-of-object--item"
      :data-row="rowIndex"
    >
      <div
        class="card-body input-array-of-object--item-inner _p-2"
        :class="wrapperClass"
      >
        <div
          class="child-row-controls"
          v-if="enableControl"
        >
          <div class="btn-group">
            <span
              v-if="isRowAddableState"
              class="btn text-success child-row-controls--add-row"
              :data-row="rowIndex"
              @click="addRow(1, rowIndex)"
            >
              <ficon
                type="plus"
                :strokeWidth="4"
                class="mx-0"
              />
            </span>
            <span
              v-if="rowLength > 1 && !disableMove"
              class="btn text-black child-row-controls--move-up"
              @click="moveUp(rowIndex)"
            >
              <ficon
                type="arrow-up"
                :strokeWidth="3"
                class="mx-0"
              />
            </span>
            <span
              v-if="rowLength > 1 && !disableMove"
              class="btn text-black child-row-controls--move-down"
              @click="moveDown(rowIndex)"
            >
              <ficon
                type="arrow-down"
                :strokeWidth="3"
                class="mx-0"
              />
            </span>
            <span
              v-if="!isMinLength && !disableRemoveRow"
              class="btn text-danger child-row-controls--remove-row"
              @click="removeRow(rowIndex)"
            >
              <ficon
                type="trash"
                :strokeWidth="4"
                class="mx-0"
              />
            </span>
          </div>
        </div>
        <oneRowOfArrayOfObjectInput
          :readOnlyMode="readOnlyMode || disableEdit"
          :ModelFormService="ModelFormService"
          :model-value="row"
          @update:one-row-array-of-object="change"
          :row="row"
          :col="col"
          :rowIndex="rowIndex"
          :recordRoot="recordRoot"
          :columnsForOneRow="columns"
          class="row"
          :modelName="modelName"
          :virtualModelName="virtualModelName"
        />
      </div>
    </div>
    <div
      v-if="enableControl"
      class="pt-2 input-array-of-object--add-row"
    >
      <span
        v-if="isRowAddableState"
        class="btn btn-sm btn-outline-primary input-array-of-object--add-row-button"
        @click="addRow(1)"
        >{{ addRowButtonLabel || addRowButtonLabelDefault }}</span
      >
    </div>
  </div>
</template>

<script lang="ts">
// dependent on "../modelInput"
import { PropType } from 'vue'
import { createNewRecordWithColumnDefs } from '../../../common/$models/createNewRecordWithColumnDefs'
import { ColumnDef, ColumnDefByColName } from '../../../common/$models/ModelDef'

export default {
  name: 'ArrayOfObject',
  props: {
    modelValue: { required: true },
    col: { required: true, type: Object as PropType<ColumnDef> },
    record: { required: true },
    commonAttrs: { required: false },
    recordRoot: {},
    readOnlyMode: { required: false, default: null },
    ModelFormService: {},
    modelName: {},
    virtualModelName: {},
    // @deprecated
    disableEdit: { type: Boolean, default: false },
    shouldDisabled: { type: Boolean, default: false },
    disableAddRow: { type: Boolean, default: false },
    disableRemoveRow: { type: Boolean, default: false },
    disableMove: { type: Boolean, default: false },
    addRowButtonLabel: { type: String, default: null },
  },
  data() {
    return {
      initialized: false,
      v: [],
      newRowValuesCached: null,
      error: {},
    }
  },
  computed: {
    columns(): ColumnDefByColName {
      if (this.col.type === 'RELATIONSHIP_ONE_TO_MANY') {
        const modelName = this.col.relationshipOneToMany?.collectionName
        return $core.$models[modelName].columns
      }
      return Object.keys(this.col.columns || {}).reduce((res, colName) => {
        return {
          ...res,
          [colName]: { ...this.col.columns[colName], name: colName },
        }
      }, {})
    },
    rowLength() {
      return this.v?.length || 0
    },
    minLength() {
      return this.col.minValueLength !== undefined ? this.col.minValueLength : 0
    },
    isMinLength() {
      return this.minLength && this.minLength >= this.rowLength
    },
    enableControl() {
      return (
        this.commonAttrs.enableControl !== false &&
        this.readOnlyMode !== true &&
        !this.disableEdit &&
        !this.shouldDisabled
      )
    },
    wrapperClass() {
      return (
        $core.$utils.findParentVueComponentByComponentName(this, 'ModelForm')
          ?.modelFormStyleClass || ''
      )
    },
    isRowAddableState() {
      if (this.disableAddRow) {
        return false
      }
      return !this.col.maxValueLength || this.rowLength < this.col.maxValueLength
    },
    valueEmittable() {
      // tempKey を delete した this.v[]
      return this.v.map((row) => {
        const { tempKey, ...rest } = row
        return rest
      })
    },
    addRowButtonLabelDefault() {
      return `+ ${this.col.label || this.col.name} のデータを追加`
    },
  },
  watch: {
    modelValue: {
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          // validate
          const isValid = Array.isArray(newValue)
          this.v = isValid ? newValue : []
        }
      },
      immediate: true,
    },
  },
  mounted() {
    const v = Array.isArray(this.modelValue) ? this.modelValue : []
    for (let value of v) {
      if (!value.tempKey) {
        value.tempKey = Math.random()
      }
    }
    this.v = v
    this.$nextTick(() => {
      const addRowNumbers = this.minLength - this.rowLength
      if (addRowNumbers > 0) {
        if (addRowNumbers) {
          this.addRow(addRowNumbers)
        }
      }
      this.initialized = true
    })
  },
  methods: {
    change(rowIndex, { value, error, modelInputVm }, subColumn, keyName) {
      const oldValue = this.v[rowIndex][keyName]
      this.v[rowIndex][keyName] = value
      // arrayObObjectのeditCallback暫定対応
      if (typeof subColumn.editCallback === 'function' && value !== oldValue) {
        // TODO: 右記じゃなくていいらしい... => this.v[rowIndex] = subColumn.editCallback({
        subColumn.editCallback({
          row: this.v[rowIndex],
          key: keyName,
          newValue: value,
          oldValue: oldValue,
          isNewRecord: this.isNewRecord,
          callerVueInstance: this,
        })
      }
      this.setError(error, rowIndex, subColumn, keyName)
      this.$nextTick(() => {
        this._changeCallback()
      })
    },
    // TODO: Error 伝える
    setError(error, rowIndex, subColumn, keyName) {
      // console.log({ error, rowIndex, subColumn, keyName })
      // debugger
    },
    async addRow(rows = 1, insertPosition = null) {
      if (rows <= 0) {
        return // do nothing
      }
      let newRows = []
      const currentVal = this.v?.length && Array.isArray(this.v) ? [...this.v] : []
      if (insertPosition === null) {
        insertPosition = currentVal.length
      }
      if (!this.newRowValuesCached) {
        this.newRowValuesCached = await this.newRowValues()
      }
      for (let i = 0; i < rows; i++) {
        const newRowValues = JSON.parse(JSON.stringify(this.newRowValuesCached))
        newRows.push(Object.assign({ tempKey: Math.random() }, newRowValues))
      }
      currentVal.splice(insertPosition, 0, ...newRows)
      this.v = currentVal
      this.$nextTick(() => {
        this._changeCallback()
      })
    },
    async newRowValues() {
      return createNewRecordWithColumnDefs(this.columns)
    },
    removeRow(rowIndex) {
      const currentVal = [...this.v]
      currentVal.splice(rowIndex, 1)
      this.v = currentVal
      this._changeCallback()
    },
    _changeCallback() {
      this.$emit('update:value-and-error', {
        value: this.valueEmittable,
        // error: this.error,
      })
    },
    moveUp(rowIndex) {
      if (rowIndex < 1) {
        return // do nothing
      }
      const arr = [...this.v]
      arr.splice(rowIndex - 1, 2, arr[rowIndex], arr[rowIndex - 1])
      this.reOrder(arr)
      this.v = arr
      this.scroll(rowIndex - 1, true)
      this.$nextTick(() => {
        this._changeCallback()
      })
    },
    moveDown(rowIndex) {
      if (rowIndex >= this.rowLength - 1) {
        return // do nothing
      }
      const arr = [...this.v]
      arr.splice(rowIndex, 2, arr[rowIndex + 1], arr[rowIndex])
      this.reOrder(arr)
      this.v = arr
      this.scroll(rowIndex + 1, false)
      this.$nextTick(() => {
        this._changeCallback()
      })
    },
    reOrder(currentVal) {
      currentVal.forEach((row) => {
        if (!row.tempKey) {
          row.tempKey = Math.random()
        }
      })
    },
    scroll(newRowIndex, isUp = true) {
      // $refs.arrayOfObjectWrapper を利用した selector に変更
      const element = this.$refs.arrayOfObjectWrapper.querySelector(
        `[data-row="${newRowIndex}"]`,
      ) as HTMLElement
      // 親要素 `.modal.show` もしくは window を取得
      const scrollElement =
        this.$refs.arrayOfObjectWrapper.closest('.modal.show') || window
      scrollElement.scrollBy({
        top: isUp ? -element.clientHeight : element.clientHeight,
        left: 0,
        behavior: 'smooth',
      })
    },
  },
}
</script>
